/** Privacy boundary for logs, telemetry, and public error payloads. */
const SENSITIVE_KEY =
  /(?:password|passwd|secret|token|authorization|cookie|set-cookie|api[-_]?key|credential|private[-_]?key|access[-_]?key|refresh[-_]?token|prompt|completion|result|response[-_]?body|message[-_]?text|email|phone|mobile|weixin|wechat|openid|unionid|address|real[-_]?name|student[-_]?name|staff[-_]?name|content|text)/i;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE = /(?<!\d)(?:\+?\d[\d\s().-]{7,}\d)(?!\d)/g;
const BEARER = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const URL_SECRET = /([?&](?:token|access_token|secret|api_key|key)=)[^&\s]+/gi;
const ISO_DATE_OR_TIMESTAMP = /(\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z)?\b)/g;
const MAX_STRING = 512;

export const REDACTED = "[REDACTED]";

export function redactString(value: string): string {
  return value
    .split(ISO_DATE_OR_TIMESTAMP)
    .map((part, index) =>
      index % 2 === 1
        ? part
        : part
            .replace(BEARER, `Bearer ${REDACTED}`)
            .replace(URL_SECRET, `$1${REDACTED}`)
            .replace(EMAIL, REDACTED)
            .replace(PHONE, REDACTED),
    )
    .join("")
    .slice(0, MAX_STRING);
}

export function redactValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === "string") return redactString(value);
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (value instanceof Error) return { name: value.name, message: redactString(value.message) };
  if (Array.isArray(value)) return value.map((item) => redactValue(item, seen));
  const output: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    output[key] = SENSITIVE_KEY.test(key) ? REDACTED : redactValue(item, seen);
  }
  return output;
}

export function redactAttributes(
  attrs?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  return attrs ? (redactValue(attrs) as Record<string, unknown>) : undefined;
}

/**
 * Return a sink-safe, non-domain exception representation.
 *
 * This deliberately does not manufacture another native Error: redaction is a
 * serialization boundary, not a domain failure boundary. The original error
 * remains available to the caller for identity/deduplication decisions.
 */
export type RedactedError = {
  readonly name: string;
  readonly message: string;
  readonly stack?: string;
};

export function redactError(error: Error): RedactedError {
  return {
    name: error.name,
    message: redactString(error.message),
    ...(error.stack ? { stack: redactString(error.stack) } : {}),
  };
}

export function publicErrorDetails(value: unknown): unknown {
  return redactValue(value);
}
